/* Auth state for the player SPA — the one piece of shared global state.
 *
 * The session is an HttpOnly cookie the SPA can't read, so we hydrate from
 * /api/auth/me on mount and expose the user plus a refresh()/logout() pair.
 * Components read it with useAuth(). The login UI itself lives in AuthModal;
 * this context just tracks who's signed in and re-fetches after auth actions. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { auth, type AuthUser, type AuthIdentity } from './api'

interface AuthState {
  user: AuthUser | null
  identities: AuthIdentity[]
  loading: boolean          // true until the first /me resolves
  refresh: () => Promise<AuthUser | null>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [identities, setIdentities] = useState<AuthIdentity[]>([])
  const [loading, setLoading] = useState(true)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const refresh = useCallback(async () => {
    try {
      const me = await auth.me()
      if (!mounted.current) return me.user
      setUser(me.user)
      setIdentities(me.identities ?? [])
      return me.user
    } catch {
      if (mounted.current) { setUser(null); setIdentities([]) }
      return null
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try { await auth.logout() } catch { /* ignore */ }
    if (mounted.current) { setUser(null); setIdentities([]) }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  // Stable value object so consumers don't re-render on every provider render.
  const value = useMemo(
    () => ({ user, identities, loading, refresh, logout }),
    [user, identities, loading, refresh, logout])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used within <AuthProvider>')
  return v
}
